import React from "react";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import supabase from "./supabaseClient";

// Logos
import cmuLogo from "./assets/CMU Logo.png";
import emoryLogo from "./assets/emory-university-logo-png_seeklogo-486660.png";
import michiganLogo from "./assets/Michigan-Logo.png";
import bucknellLogo from "./assets/university-of-maryland-1-logo-png-transparent.png";
import tuftsLogo from "./assets/Tufts_University_logo.png";

export default function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const isAuthenticated = !!user;

  const navItems = [
    { name: "Extracurriculars", link: "/admissionstoolkit" },
    { name: "Saved Activities", link: "/profile" },
  ];

  // Reusable styling class matched exactly with your navbar items
  const baseBtnClass = "text-sm font-semibold transition-all duration-200 px-4 py-2 rounded-xl text-slate-700 hover:bg-white/70 hover:text-slate-950";
  const activeBtnClass = "text-sm font-semibold transition-all duration-200 px-4 py-2 rounded-xl bg-white text-slate-900 shadow-sm";

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#edf4ff] text-slate-700">
        Loading...
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#edf4ff] text-slate-900">

      {/* ===== BACKGROUND ===== */}

      {/* Base gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_#ffffff,_#dbeafe_35%,_#c7dcff_65%,_#b5cbf4_100%)]" />

      {/* Moving shine */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="shine-overlay" />
      </div>

      {/* Floating glow orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="orb orb1" />
        <div className="orb orb2" />
        <div className="orb orb3" />
      </div>

      {/* Grid texture */}
      <div className="absolute inset-0 opacity-[0.04] bg-[linear-gradient(to_right,#1e3a5f_1px,transparent_1px),linear-gradient(to_bottom,#1e3a5f_1px,transparent_1px)] bg-[size:70px_70px]" />

      {/* Noise */}
      <div className="absolute inset-0 noise opacity-[0.035] mix-blend-overlay" />

      {/* ===== NAVBAR ===== */}
      <nav className="absolute top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-7 flex items-center justify-between">

          {/* Logo */}
          <div
            onClick={() => navigate("/")}
            className="flex items-center gap-3 cursor-pointer"
          >
            <div className="w-9 h-9 rounded-2xl bg-[#1e3a5f] text-white flex items-center justify-center font-black shadow-lg">
              C
            </div>

            <div className="font-bold tracking-tight text-lg">
              CollegeNavi
            </div>
          </div>

          {/* Nav */}
          <div className="flex items-center gap-3 md:gap-5">

            {navItems.map((item) => {
              const active = location.pathname === item.link;

              return (
                <Link
                  key={item.name}
                  to={item.link}
                  className={active ? activeBtnClass : baseBtnClass}
                >
                  {item.name}
                </Link>
              );
            })}

            {isAuthenticated ? (
              <button
                onClick={handleLogout}
                className="text-sm font-semibold text-red-600 hover:text-red-700 px-4 py-2"
              >
                Logout
              </button>
            ) : (
              <Link
                to="/login"
                className={location.pathname === "/login" ? activeBtnClass : baseBtnClass}
              >
                Log In
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* ===== HERO ===== */}
      <section className="relative z-10 min-h-screen flex items-center justify-center px-6">

        <div className="max-w-5xl text-center">

          {/* Heading */}
          <motion.h1
            className="mt-14 text-5xl md:text-7xl font-black leading-[1.05] tracking-tight"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          >
            <br></br>
            <div className="flex flex-col items-center leading-[1.05]">
              <span>Build a stronger</span>
              {/* Updated class below for the glaze sweep */}
              <span className="mt-2 inline-block text-transparent bg-clip-text text-glaze-sweep">
                college journey.
              </span>
            </div>
          </motion.h1>

          {/* Subtext */}
          <motion.p
            className="mt-8 max-w-2xl mx-auto text-base md:text-lg leading-relaxed text-slate-600 font-medium"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
          >
            Discover extracurriculars, organize achievements,
            and track your growth with a cleaner and more focused
            admissions experience.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-8"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <button
              onClick={() => navigate("/admissionstoolkit")}
              className={`text-sm font-semibold px-5 py-2.5 rounded-xl
              border border-black/30
              bg-white/60 backdrop-blur-md
              shadow-[0_8px_30px_rgba(0,0,0,0.08)]
              hover:bg-white/80 hover:border-black/40
              transition-all duration-200`}
            >
              Explore Activities
            </button>

            <button
              onClick={() => navigate("/profile")}
              className={`text-sm font-semibold px-5 py-2.5 rounded-xl
              border border-black/30
              bg-white/60 backdrop-blur-md
              shadow-[0_8px_30px_rgba(0,0,0,0.08)]
              hover:bg-white/80 hover:border-black/40
              transition-all duration-200`}
            >
              View Dashboard
            </button>
          </motion.div>

          {/* University logos */}
          <motion.div
            className="mt-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35 }}
          >
            <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 font-bold">
              Inspired by admissions guidance from
            </div>

            <div className="mt-8 flex flex-wrap justify-center items-center gap-10 opacity-60 grayscale">
              {[cmuLogo, emoryLogo, michiganLogo, bucknellLogo, tuftsLogo].map(
                (logo, i) => (
                  <img
                    key={i}
                    src={logo}
                    alt="University Logo"
                    className="h-7 object-contain hover:grayscale-0 transition duration-300"
                  />
                )
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== ABOUT SECTION ===== */}
      <section className="relative z-10 px-6 pb-24">
        <div className="max-w-5xl mx-auto">

          <div className="bg-white/65 backdrop-blur-2xl border border-white/60 rounded-[32px] p-10 md:p-14 shadow-[0_20px_80px_rgba(30,58,95,0.12)]">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight">
              About CollegeNavi
            </h2>

            <p className="mt-5 text-slate-600 leading-relaxed max-w-3xl text-sm md:text-base">
              CollegeNavi helps students discover meaningful
              extracurricular opportunities, organize achievements,
              and stay focused throughout the admissions process —
              all through a clean, modern interface designed to
              reduce stress and improve clarity.
            </p>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <div className="fixed bottom-5 right-5 z-50">
        <div className="flex items-center gap-3 bg-white/75 backdrop-blur-xl border border-white/70 rounded-2xl px-4 py-3 shadow-xl">
          <button
            onClick={() => navigate("/privacy")}
            className="text-xs font-medium text-slate-600 hover:text-slate-900"
          >
            Privacy
          </button>

          <div className="w-px h-4 bg-slate-300" />

          <button
            onClick={() => navigate("/public")}
            className="text-xs font-medium text-slate-600 hover:text-slate-900"
          >
            Policy
          </button>
        </div>
      </div>

      {/* ===== STYLES ===== */}
      <style>{`

        .noise {
          background-image: url("https://www.transparenttextures.com/patterns/noise.png");
        }

        /* Cleaned up Text Glaze Sweep effect using shifting backgrounds */
        .text-glaze-sweep {
          background-image: linear-gradient(
            110deg,
            #1e3a5f 35%,
            #8bb4f7 48%,
            #ffffff 50%,
            #8bb4f7 52%,
            #1e3a5f 65%
          );
          background-size: 200% 100%;
          animation: glaze 3s linear infinite;
        }

        @keyframes glaze {
          0% {
            background-position: 100% 0;
          }
          100% {
            background-position: -100% 0;
          }
        }

        /* Glow orbs */
        .orb {
          position: absolute;
          border-radius: 9999px;
          filter: blur(90px);
          opacity: 0.45;
        }

        .orb1 {
          width: 420px;
          height: 420px;
          background: #60a5fa;
          top: 8%;
          left: -5%;
          animation: float1 16s ease-in-out infinite;
        }

        .orb2 {
          width: 340px;
          height: 340px;
          background: #93c5fd;
          bottom: -8%;
          right: -2%;
          animation: float2 20s ease-in-out infinite;
        }

        .orb3 {
          width: 260px;
          height: 260px;
          background: #dbeafe;
          top: 45%;
          left: 50%;
          animation: float3 18s ease-in-out infinite;
        }

        @keyframes float1 {
          0%, 100% {
            transform: translate(0px, 0px);
          }
          50% {
            transform: translate(40px, -30px);
          }
        }

        @keyframes float2 {
          0%, 100% {
            transform: translate(0px, 0px);
          }
          50% {
            transform: translate(-30px, 30px);
          }
        }

        @keyframes float3 {
          0%, 100% {
            transform: translate(0px, 0px);
          }
          50% {
            transform: translate(20px, -25px);
          }
        }

      `}</style>
    </div>
  );
} 